import { useEffect, useCallback, useState, useMemo } from 'react'
import { useSubscription } from 'streamr-client-react'
import useIsMounted from '~hooks/useIsMounted'

const hourlyStreamId = 'l2hQ_d3PTYOj4xbD1NpU3A'

const dailyStreamId = '1Rj74LcjRlW9W50G8qOHwA'

const txHistoricalGraphPoints = (dataPoints) => (
    dataPoints.map((dataPoint, index) => ({
        x: index,
        y: dataPoint,
    }))
)

const txLatestGraphPoint = (dataPoint) => (
    {
        x: undefined,
        y: dataPoint,
    }
)

const emptyPoint = txLatestGraphPoint(undefined)

export default () => {
    const isMounted = useIsMounted()

    const [includedMembersHistory, setIncludedMembersHistory] = useState(false)
    const [includedCountsHistory, setIncludedCountsHistory] = useState(false)
    const [includedEarningsHistory, setIncludedEarningsHistory] = useState(false)
    const [includedPurchaseCountsHistory, setIncludedPurchaseCountsHistory] = useState(false)
    const [dates, setDates] = useState([])

    const [latestDate, setLatestDate] = useState('')
    const [latestMembers, setLatestMembers] = useState(emptyPoint)
    const [latestCount, setLatestCount] = useState(emptyPoint)
    const [latestEarnings, setLatestEarnings] = useState(emptyPoint)
    const [latestPurchaseCount, setLatestPurchaseCount] = useState(emptyPoint)

    const [historicalMembers, setHistoricalMembers] = useState([])
    const [historicalCounts, setHistoricalCounts] = useState([])
    const [historicalEarnings, setHistoricalEarnings] = useState([])
    const [historicalPurchaseCounts, setHistoricalPurchaseCounts] = useState([])

    const onHourlyMessage = useCallback(({
        members: hourlyMembers,
        count: hourlyCount,
        earnings: hourlyEarnings,
        purchaseCount: hourlyPurchaseCount,
        date,
    }) => {
        if (isMounted()) {
            setLatestMembers(txLatestGraphPoint(hourlyMembers))
            setLatestCount(txLatestGraphPoint(hourlyCount))
            setLatestEarnings(txLatestGraphPoint(hourlyEarnings))
            setLatestPurchaseCount(txLatestGraphPoint(hourlyPurchaseCount))
            setLatestDate(date)
        }
    }, [isMounted])

    const onDailyMessage = useCallback(({
        members: dailyMembers,
        counts: dailyCounts,
        earnings: dailyEarnings,
        purchaseCounts: dailyPurchaseCounts,
        dates: dailyDates,
    }) => {
        if (isMounted()) {
            setHistoricalMembers(txHistoricalGraphPoints(dailyMembers))
            setHistoricalCounts(txHistoricalGraphPoints(dailyCounts))
            setHistoricalEarnings(txHistoricalGraphPoints(dailyEarnings))
            setHistoricalPurchaseCounts(txHistoricalGraphPoints(dailyPurchaseCounts))
            setDates(dailyDates)
        }
    }, [isMounted])

    // Only add latest DataPoints to the historical points if the historical series has been already added,
    // and only if the latest DataPoint has not already been added.

    useEffect(() => {
        if (latestDate && dates.length && dates[dates.length - 1] !== latestDate) {
            setDates([...dates, latestDate])
        }
    }, [dates, latestDate])

    useEffect(() => {
        if (!includedMembersHistory && latestMembers.y) {
            setIncludedMembersHistory(true)

            setHistoricalMembers([...historicalMembers, {
                x: historicalMembers.length, y: latestMembers.y,
            }])
        }
    }, [includedMembersHistory, historicalMembers, latestMembers.y])

    useEffect(() => {
        if (!includedCountsHistory && latestCount.y) {
            setIncludedCountsHistory(true)

            setHistoricalCounts([...historicalCounts, {
                x: historicalCounts.length, y: latestCount.y,
            }])
        }
    }, [includedCountsHistory, historicalCounts, latestCount.y])

    useEffect(() => {
        if (!includedEarningsHistory && latestEarnings.y) {
            setIncludedEarningsHistory(true)

            setHistoricalEarnings([...historicalEarnings, {
                x: historicalEarnings.length, y: latestEarnings.y,
            }])
        }
    }, [includedEarningsHistory, historicalEarnings, latestEarnings.y])

    useEffect(() => {
        if (!includedPurchaseCountsHistory && latestPurchaseCount.y) {
            setIncludedPurchaseCountsHistory(true)

            setHistoricalPurchaseCounts([...historicalPurchaseCounts, {
                x: historicalPurchaseCounts.length, y: latestPurchaseCount.y,
            }])
        }
    }, [includedPurchaseCountsHistory, historicalPurchaseCounts, latestPurchaseCount.y])

    useSubscription({
        stream: hourlyStreamId,
        resend: {
            last: 1,
        },
    }, {
        onMessage: onHourlyMessage,
    })

    useSubscription({
        stream: dailyStreamId,
        resend: {
            last: 1,
        },
    }, {
        onMessage: onDailyMessage,
    })

    return useMemo(() => ({
        dates,
        historicalCounts,
        historicalEarnings,
        historicalMembers,
        historicalPurchaseCounts,
    }), [
        dates,
        historicalCounts,
        historicalEarnings,
        historicalMembers,
        historicalPurchaseCounts,
    ])
}
